import { useMemo, useState } from 'react';
import { Link2, Network, ShieldHalf, Trash2 } from 'lucide-react';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { CompactStat, CompactStatGroup } from '../components/CompactStat';
import {
  Button,
  IconButton,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useVpnSourceNats,
  useVpnGatewayReady,
  useS2sConnections,
  useVpnGatewaySubnets,
  useAttachGatewaySubnet,
  useDetachGatewaySubnet,
  useUpdateVpnGateway,
  useNetworkList,
  networkCidr,
  type VpnGateway,
  type VpnConnection,
} from '@/api/opennebula';
import { DescList, DescItem, monoValue } from '../components/DescList';

/** Edit a VPN gateway's name + description (matches @console gateway edit). */
export function GatewayEditDialog({ serviceId, gateway, onClose }: { serviceId: string; gateway: VpnGateway; onClose: () => void }) {
  const { show } = useToast();
  const [name, setName] = useState(gateway.name ?? '');
  const [description, setDescription] = useState('');
  const update = useUpdateVpnGateway(serviceId);

  const save = () =>
    update.mutate(
      { id: gateway.id, payload: { name: name.trim(), description: description.trim() || undefined } },
      {
        onSuccess: () => { show({ tone: 'success', title: 'Gateway updated' }); onClose(); },
        onError: () => show({ tone: 'danger', title: 'Update failed' }),
      },
    );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit VPN gateway</DialogTitle></DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Field label="Name">{(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="vpn-gateway-prod" />}</Field>
          <Field label="Description">{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />}</Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={!name.trim() || update.isPending} onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** VPN gateway detail: Overview (info + readiness + source NAT), Connections
 *  (S2S bound to this gateway) and Subnets (gateway-scoped network binding). */
export function GatewayDetailDialog({ serviceId, gateway, onClose }: { serviceId: string; gateway: VpnGateway; onClose: () => void }) {
  // The gateway is provisioned asynchronously (VyOS appliance + public IP). While
  // it isn't ready yet, poll the readiness endpoint so the dialog reflects live
  // progress instead of a stale list value; stop once it reports ready.
  const provisioning = gateway.status !== 'ready';
  const { data: readiness } = useVpnGatewayReady(serviceId, gateway.id, provisioning);
  const ready = readiness?.ready === true || gateway.status === 'ready';
  const liveState = readiness?.state ?? gateway.status;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              {gateway.name || gateway.id}
              <StatusPill tone={ready ? 'success' : 'muted'}>{ready ? 'Ready' : liveState || 'Unknown'}</StatusPill>
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="connections">Connections</TabsTrigger>
              <TabsTrigger value="subnets">Subnets</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <OverviewTab serviceId={serviceId} gateway={gateway} ready={ready} liveState={liveState} />
            </TabsContent>
            <TabsContent value="connections">
              <ConnectionsTab serviceId={serviceId} gateway={gateway} />
            </TabsContent>
            <TabsContent value="subnets">
              <SubnetsTab serviceId={serviceId} gateway={gateway} ready={ready} />
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter><Button variant="secondary" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverviewTab({ serviceId, gateway, ready, liveState }: { serviceId: string; gateway: VpnGateway; ready: boolean; liveState?: string }) {
  const { data: nats = [] } = useVpnSourceNats(serviceId, gateway.id);
  const readyState = ready ? 'Ready' : liveState ? `${liveState}…` : 'Provisioning…';

  // Dialog-constrained surface: framed StatGroup + identity DescList without a
  // SplitView rail (no room for one inside the 640px dialog).
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          {/* Status lives in the dialog title pill — no duplicate KPI here. */}
          <CompactStatGroup columns={2}>
            <CompactStat label="Source NAT rules" value={nats.length} />
            <CompactStat label="Public IP" value={gateway.public_ip ? <code style={monoValue}>{gateway.public_ip}</code> : '—'} />
          </CompactStatGroup>
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader><CardTitle>Gateway information</CardTitle></CardHeader>
        <CardBody>
          <DescList minColumn={180}>
            <DescItem label="Public IP">{gateway.public_ip ? <code style={monoValue}>{gateway.public_ip}</code> : '—'}</DescItem>
            <DescItem label="Readiness">{readyState}</DescItem>
            <DescItem label="Gateway ID"><code style={monoValue}>{gateway.id}</code></DescItem>
          </DescList>
        </CardBody>
      </Card>

      <div>
        <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>Source NAT rules</div>
        {nats.length === 0 ? (
          <EmptyState icon={<ShieldHalf size={20} />} title="No source NAT rules yet" description="Manage source NAT rules in the VPN › Source NAT tab." />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {nats.map((n) => (
              <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--vn-font-size-sm)', padding: 'var(--vn-space-1) 0', borderBottom: '1px solid var(--vn-border-default)' }}>
                <span>{n.name || n.id}</span>
                <code style={{ color: 'var(--vn-fg-muted)' }}>{n.source_addr} → {n.translation_addr}</code>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionsTab({ serviceId, gateway }: { serviceId: string; gateway: VpnGateway }) {
  const { data: all = [] } = useS2sConnections(serviceId);
  const connections = useMemo(() => all.filter((c) => c.vpn_gateway_id === gateway.id), [all, gateway.id]);

  const columns: ColumnDef<VpnConnection, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'peer', header: 'Remote gateway', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>{row.original.remote_gateway_ip || '—'}</span> },
    { id: 'local', header: 'Local subnets', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.local_subnet_cidrs || '—'}</span> },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={/up|active|connect/i.test(row.original.status ?? '') ? 'success' : 'muted'}>{row.original.status || 'Unknown'}</StatusPill> },
  ];

  // Empty placeholder renders inside the table's own bordered frame (via
  // `emptyState`), so the zone keeps the same frame whether empty or full.
  return (
    <DataTable
      columns={columns}
      data={connections}
      getRowId={(c) => String(c.id)}
      emptyState={<EmptyState icon={<Link2 size={20} />} title="No connections" description="No site-to-site connections use this gateway yet." />}
    />
  );
}

function SubnetsTab({ serviceId, gateway, ready }: { serviceId: string; gateway: VpnGateway; ready: boolean }) {
  const { show } = useToast();
  // GET is default-disabled (backend endpoint may not be live); mirrors @console's
  // guarded behaviour. The attach list below drives the binding regardless.
  const { data: attached = [] } = useVpnGatewaySubnets(serviceId, gateway.id);
  const { data: networks = [] } = useNetworkList(serviceId);

  const attach = useAttachGatewaySubnet(serviceId, gateway.id, {
    onSuccess: () => show({ tone: 'success', title: 'Subnet attached' }),
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });
  const detach = useDetachGatewaySubnet(serviceId, gateway.id, {
    onSuccess: () => show({ tone: 'success', title: 'Subnet detached' }),
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  const attachedIds = new Set(attached.map((s) => String(s.network_id)));
  const available = networks.filter((n) => !attachedIds.has(String(n.id)));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>Attached subnets</div>
        {attached.length === 0 ? (
          <EmptyState icon={<Network size={20} />} title="No subnets attached" description="Attach a private network to route traffic through this gateway." />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {attached.map((s) => (
              <div key={String(s.network_id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--vn-space-1) 0', borderBottom: '1px solid var(--vn-border-default)' }}>
                <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{s.name || s.network_id} {s.cidr && <code style={{ color: 'var(--vn-fg-muted)' }}>· {s.cidr}</code>}</span>
                <IconButton variant="ghost" size="sm" aria-label="Detach subnet" disabled={detach.isPending} onClick={() => detach.mutate({ network_id: String(s.network_id), nic_id: Number(s.nic_id ?? 0) })}>
                  <Trash2 size={15} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>Attach a private network</div>
        {!ready ? (
          <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>The gateway must be ready before attaching subnets.</p>
        ) : available.length === 0 ? (
          <EmptyState icon={<Network size={20} />} title="No networks available" description="All private networks are already attached to this gateway." />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {available.map((n) => {
              const c = networkCidr(n);
              return (
                <div key={n.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--vn-space-1) 0', borderBottom: '1px solid var(--vn-border-default)' }}>
                  <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{n.name || n.id} {c && <code style={{ color: 'var(--vn-fg-muted)' }}>· {c}</code>}</span>
                  <Button size="sm" variant="secondary" disabled={attach.isPending} onClick={() => attach.mutate({ network_id: String(n.id) })}>Attach</Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
